<template>
    <div>
        <worklog-filters @filters-updated="applyFilters" />
        <section>
            <div v-for="log in worklogs" :key="log.id" class="block mb-4">
                <div class="box panel hover:shadow-lg transition-shadow duration-200">
                    <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                        <div>
                            <p class="is-size-7 has-text-grey">
                                ID: #{{ log.id }}
                            </p>
                            <p class="has-text-weight-semibold">
                                {{ $t('date') }} {{ log.created_at }}
                            </p>
                        </div>
                        <div>
                            <b-tag :type="mapEntityColor(log.entity_type)" size="is-medium" class="is-capitalized">
                                {{ $t(mapEntityLabel(log.entity_type)) }}
                            </b-tag>
                            <b-tag :type="mapActionColor(log.action_type)" size="is-medium" class="is-capitalized">
                                <b-icon pack="fas" :icon="mapActionIcon(log.action_type)" size="is-small"
                                    class="mr-1" />
                                {{ $t(mapActionLabel(log.action_type)) }}
                            </b-tag>
                        </div>
                    </div>

                    <p class="mt-2">
                        <!-- <strong>{{ log.user }}</strong> -->
                        {{ $t('action_performed') }}:
                        <strong>{{ $t(mapActionLabel(log.action_type)) }}</strong>
                        {{ $t('regarding') }}:
                        <strong v-if="log.entity_type === 4"> {{ $t(mapEntityLabel(log.entity_type)) }}</strong>
                        <template v-if="log.entity_type === 4" class="my-2">
                            <p class="mt-2 has-text-grey is-size-7 mb-2">
                                {{ $t('project') }}: <strong>{{ log.project || $t('not_available_short') }}</strong>
                            </p>
                            <notification-comment
                                :notification="{ comment: log.entity_full, activity: log.entity_full.activity }"
                                entity-type="activity" />
                        </template>
                        <template v-else>
                            <router-link :to="getLogRoute(log)">
                                <strong class="has-text-link">{{ $t(mapEntityLabel(log.entity_type)) }} : {{ log.entity_name }}</strong>
                            </router-link>
                            <p class="mt-2 has-text-grey is-size-7">
                                {{ $t('project') }}: <strong>{{ log.project || $t('not_available_short') }}</strong>
                            </p>
                        </template>
                    </p>

                    <div v-if="log.entity_type === 3">
                        <div v-if="log.entity_full" class="flex items-end justify-end mb-2">
                            <entity-document-thumbnail :key="log.entity_full.id" :document="log.entity_full"
                                :parent-id="log.entity_id" entity-type="activity" size="small" />
                        </div>
                        <div v-else class="flex items-start mb-2">
                            <span>{{ $t('deleted_document') }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <!-- PAGINADOR -->
            <div class="is-flex is-justify-content-center mt-6">
                <b-pagination v-model="currentPage" :total="total" :per-page="perPage" :limit="5" rounded
                    size="is-small" icon-pack="fas" prev-icon="chevron-left" next-icon="chevron-right" />
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios'
import WorklogFilters from './worklog-filters.vue'
import entityDocumentThumbnail from '../../../components/entity-document-thumbnail.vue'
import notificationComment from '../../../components/notifications/notification-components/notification-comment.vue'

import { size } from 'lodash'

export default {
    title: 'worklog_history_title',
    subtitle: 'worklog_history_subtitle',
    menuOption: '0-2',
    data() {
        return {
            worklogs: [],
            currentPage: 1,
            perPage: 20,
            total: 0,
            activeFilters: {},
        }
    },
    components: {
        WorklogFilters,
        entityDocumentThumbnail,
        notificationComment
    },
    computed: {
        userId() {
            return this.$route.params.id
        }
    },
    methods: {
        async fetchWorklogs(filters = null) {
            const params = {
                ...this.activeFilters,
                per_page: this.perPage,
                page: this.currentPage,
            };
            if (filters) {
                Object.assign(params, filters);
            }
            const { data } = await axios.get(
                `/api/v2/user/${this.userId}/worklog`,
                { params }
            );
            this.worklogs = data.data;
            this.total = data.meta.total;
        },

        applyFilters(filters) {
            this.currentPage = 1;
            this.activeFilters = filters || {};
            this.fetchWorklogs(filters);
        },

        getEntityRouteMap() {
            return {
                0: { name: 'activity.home', param: 'activity_id' },
                1: { name: 'subactivity.home', param: 'subactivity_id' },
                2: { name: 'issue.home', param: 'issue_id' },
                5: { name: 'project.home', param: 'project_id' },
                9: { name: 'validation.home', param: 'validation_id' }
            }
        },

        getLogRoute(log) {
            const route = this.getEntityRouteMap()[log.entity_type];
            if (route) {
                return { name: route.name, params: { [route.param]: log.entity_id } };
            }
            return { name: 'project.home', params: {} };
        },

        /**
         * Colores de <b-tag> según action_type (0–22)
         */
        mapActionColor(type) {
            switch (type) {
                case 0: return 'is-primary'     // CREATE
                case 1: return 'is-info'        // UPDATE
                case 2: return 'is-danger'      // DELETE
                case 3: return 'is-warning'     // ACTIVITY_PROGRESS
                case 4: return 'is-warning'     // ACTIVITY_STATUS
                case 5: return 'is-link'        // MENTION
                case 6: return 'is-success'     // ADD_USER
                case 7: return 'is-danger'      // REMOVE_USER
                case 8: return 'is-success'     // OPEN
                case 9: return 'is-danger'      // CLOSE
                case 10: return 'is-info'        // ACCESS
                case 11: return 'is-success'     // COMPLETED
                case 12: return 'is-info'        // IMPORT_LOCATIONS
                case 13: return 'is-info'        // IMPORT_ACTIVITIES
                case 14: return 'is-light'       // IMPORT_ACTIVITIES_SILENT
                case 15: return 'is-info'        // IMPORT_ASSIGNMENTS
                case 16: return 'is-light'       // IMPORT_ASSIGNMENTS_SILENT
                case 17: return 'is-light'       // CREATE_SILENT
                case 18: return 'is-light'       // UPDATE_SILENT
                case 19: return 'is-info'        // BULK_ACTION
                case 20: return 'is-light'       // BULK_ACTION_SILENT
                case 21: return 'is-info'        // UPLOAD_VERSION
                case 22: return 'is-info'        // ACTIVITY_PROGRESS_ACTUAL
                default: return 'is-light'
            }
        },
        mapEntityColor(type) {
            switch (type) {
                case 0: return 'is-warning'      // ACTIVITY
                case 1: return 'is-info'         // SUBACTIVITY
                case 2: return 'is-danger'       // ISSUE
                case 3: return 'is-primary'      // DOCUMENT
                case 4: return 'is-link'         // COMMENT
                case 5: return 'is-success'      // PROJECT
                case 6: return 'is-info'         // ISSUECOMMENT
                case 7: return 'is-warning'      // AREA
                case 8: return 'is-info'         // ASSIGNMENT
                case 9: return 'is-dark'         // VALIDATION
                case 10: return 'is-light'       // FOLDER
                default: return 'is-light'
            }
        },
        mapEntityLabel(type) {
            switch (type) {
                case 0: return 'activity'
                case 1: return 'subactivity'
                case 2: return 'issue'
                case 3: return 'document'
                case 4: return 'comment'
                case 5: return 'project'
                case 6: return 'issue_comment'
                case 7: return 'area'
                case 8: return 'assignment'
                case 9: return 'validation'
                case 10: return 'folder'
                default: return 'not_available_short'
            }
        },
        mapActionLabel(type) {
            switch (type) {
                case 0: return 'create'
                case 1: return 'update'
                case 2: return 'delete'
                case 3: return 'progress_label'
                case 4: return 'activity_status'
                case 5: return 'mention'
                case 6: return 'add_user'
                case 7: return 'remove_user'
                case 8: return 'opened'
                case 9: return 'close'
                case 10: return 'access_type'
                case 11: return 'completed'
                case 12: return 'import_locations'
                case 13: return 'import_activities'
                case 14: return 'import_activities'
                case 15: return 'import_assignments'
                case 16: return 'import_assignments'
                case 17: return 'create'
                case 18: return 'update'
                case 19: return 'bulk_action'
                case 20: return 'bulk_action'
                case 21: return 'upload_version'
                case 22: return 'progress_label'
                default: return 'not_available_short'
            }
        },

        /**
         * Iconos FontAwesome según action_type
         */
        mapActionIcon(type) {
            switch (type) {
                case 0: return 'plus-circle'
                case 1: return 'edit'
                case 2: return 'trash'
                case 3: return 'chart-line'
                case 4: return 'adjust'
                case 5: return 'at'
                case 6: return 'user-plus'
                case 7: return 'user-minus'
                case 8: return 'folder-open'
                case 9: return 'times-circle'
                case 10: return 'unlock'
                case 11: return 'check-circle'
                case 12: return 'map-marker-alt'
                case 13: return 'tasks'
                case 14: return 'ear-slash'
                case 15: return 'clipboard-list'
                case 16: return 'clipboard-check'
                case 17: return 'plus-circle'
                case 18: return 'edit'
                case 19: return 'layer-group'
                case 20: return 'layer-group'
                case 21: return 'upload'
                case 22: return 'chart-line'
                default: return 'question-circle'
            }
        }
    },
    watch: {
        currentPage() {
            this.fetchWorklogs()
        }
    },
    mounted() {
        this.fetchWorklogs()
    }
}
</script>

<style scoped>
.panel {
    border-top: 2px solid #3a8ee6;
}
</style>
